import { useMemo, useState } from "react";
import type { ApiError } from "@/lib/api";
import type { PlayerGoal } from "@/lib/types";
import { useMatches } from "@/features/matches/useMatches";
import { CoachModal } from "./CoachModal";
import { useRoster } from "../hooks/useRoster";
import type { CoachGoalMutations } from "../hooks/useCoachGoals";

/**
 * Player-goal composer modal — the M6a Goals surface.
 *
 * Ports the legacy `openCoachGoalModal` (`js/coaching/goals.js`).
 * `null` `goal` ⇒ create; a `PlayerGoal` ⇒ edit.
 *
 * Fields mirror `CreatePlayerGoalRequest`:
 *   - player (LOCKED on edit — `UpdatePlayerGoalRequest` omits
 *     `player_id`; a goal's player is immutable)
 *   - context · visibility · priority · status
 *   - title (required) · description (action plan)
 *   - target_date — a styled text input with a YYYY-MM-DD pattern
 *     (NOT `<input type="date">`, per the no-native-chrome rule)
 *   - success_criteria
 *   - coach_private_note — coach-only; the BACKEND scrubs it for
 *     viewers. Templating it here is fine — this is a coach
 *     surface — but it never reaches viewer DOM.
 *   - target_match_id (optional "do X by this game")
 *
 * Opened from the Roster row's "Create goal" button (with the
 * player preselected). Note/Clip "Create goal" entry points are a
 * tracked M6-polish-pass follow-up.
 *
 * The goal CARD render + the per-player goal LIST are M7 (they
 * live inside Player Development) — this slice is the modal only.
 */

const CONTEXT_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: "next_match", label: "Next match" },
  { value: "next_training", label: "Next training" },
  { value: "season_goal", label: "Season goal" },
  { value: "other", label: "Other" },
];
const VISIBILITY_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: "player", label: "Player + family" },
  { value: "coach", label: "Coach / admin only" },
];
const PRIORITY_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
];
const STATUS_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: "open", label: "Open" },
  { value: "in_progress", label: "In progress" },
  { value: "needs_follow_up", label: "Needs follow-up" },
  { value: "achieved", label: "Achieved" },
  { value: "archived", label: "Archived" },
];

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/** Source evidence the goal was created from. Set when the
 * coach opens the modal from a note / clip / playlist row's
 * "+ New goal" button — the M6a-deferred entry-points that
 * land in M7.5. Ignored in edit mode (the goal's source is
 * immutable). At most one of `source_note_id` / `source_clip_id`
 * / `source_playlist_id` is set; the modal also seeds the title
 * and description from `seed` when present. */
export type GoalComposerSource = {
  source_note_id?: number;
  source_clip_id?: number;
  source_playlist_id?: number;
  /** Optional seed title — overrides the empty default. */
  seedTitle?: string;
  /** Optional seed description — overrides the empty default. */
  seedDescription?: string;
};

export type GoalComposerModalProps = {
  /** `null` ⇒ create; a `PlayerGoal` ⇒ edit it. */
  goal: PlayerGoal | null;
  /** Preselected player for create mode (the Roster row passes
   * its player id). Ignored in edit mode (the goal's player is
   * fixed). */
  preselectedPlayerId?: string;
  /** Source evidence (M7.5 D3). Ignored in edit mode. */
  source?: GoalComposerSource;
  mutations: CoachGoalMutations;
  onClose: () => void;
  onSaved?: () => void;
};

export function GoalComposerModal({
  goal,
  preselectedPlayerId,
  source,
  mutations,
  onClose,
  onSaved,
}: GoalComposerModalProps) {
  const isEdit = goal !== null;
  const rosterQuery = useRoster();
  const matchesQuery = useMatches();
  const players = rosterQuery.data?.players ?? [];
  const matches = matchesQuery.data ?? [];

  const [playerId, setPlayerId] = useState(
    goal?.player_id ?? preselectedPlayerId ?? players[0]?.id ?? "",
  );
  const [context, setContext] = useState<string>(goal?.context ?? "next_match");
  const [visibility, setVisibility] = useState<string>(
    goal?.visibility ?? "player",
  );
  const [priority, setPriority] = useState<string>(goal?.priority ?? "medium");
  const [status, setStatus] = useState<string>(goal?.status ?? "open");
  // Seed title/description from the source evidence when the
  // modal opens from a list row's "+ New goal" button (M7.5).
  // Edit mode (goal != null) overrides both with the existing
  // values — `source` is ignored when editing.
  const [title, setTitle] = useState(
    goal ? (goal.title ?? "") : (source?.seedTitle ?? ""),
  );
  const [description, setDescription] = useState(
    goal ? (goal.description ?? "") : (source?.seedDescription ?? ""),
  );
  const [targetDate, setTargetDate] = useState(goal?.target_date ?? "");
  const [successCriteria, setSuccessCriteria] = useState(
    goal?.success_criteria ?? "",
  );
  const [coachPrivateNote, setCoachPrivateNote] = useState(
    goal?.coach_private_note ?? "",
  );
  const [targetMatchId, setTargetMatchId] = useState(
    goal?.target_match_id ?? "",
  );
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Resolve the player select default once matches/roster load.
  const resolvedPlayerId =
    playerId || (isEdit ? (goal?.player_id ?? "") : players[0]?.id ?? "");

  const matchLabelById = useMemo(() => {
    const map = new Map<string, string>();
    for (const m of matches) {
      const teams = `${m.home_team} vs ${m.away_team}`;
      map.set(m.id, m.date ? `${teams} · ${m.date}` : teams);
    }
    return map;
  }, [matches]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const t = title.trim();
    if (!isEdit && !resolvedPlayerId) {
      setError("Pick a player for this goal.");
      return;
    }
    if (!t) {
      setError("Goal title is required.");
      return;
    }
    if (targetDate && !DATE_RE.test(targetDate)) {
      setError("Target date must be YYYY-MM-DD or left blank.");
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const shared = {
        title: t,
        description: description.trim(),
        context,
        visibility,
        priority,
        status,
        target_date: targetDate,
        success_criteria: successCriteria.trim(),
        coach_private_note: coachPrivateNote.trim(),
        target_match_id: targetMatchId || null,
      };
      if (isEdit) {
        // `UpdatePlayerGoalRequest` omits `player_id` — a goal's
        // player is immutable.
        await mutations.updateGoal(goal.id, shared);
      } else {
        // M7.5 D3: pass source-evidence IDs when set. At most one
        // of these is populated; the backend (M6a `models.py`'s
        // `CreatePlayerGoalRequest`) accepts all three optionally.
        await mutations.createGoal({
          player_id: resolvedPlayerId,
          ...shared,
          ...(source?.source_note_id != null && {
            source_note_id: source.source_note_id,
          }),
          ...(source?.source_clip_id != null && {
            source_clip_id: source.source_clip_id,
          }),
          ...(source?.source_playlist_id != null && {
            source_playlist_id: source.source_playlist_id,
          }),
        });
      }
      onSaved?.();
      onClose();
    } catch (err) {
      const apiErr = err as ApiError;
      setError(
        typeof apiErr?.detail === "string"
          ? apiErr.detail
          : `Could not ${isEdit ? "update" : "create"} the goal.`,
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <CoachModal
      title={isEdit ? "Edit player goal" : "New player goal"}
      onClose={onClose}
      data-testid="goal-composer-modal"
    >
      <form className="flex flex-col gap-3" onSubmit={handleSubmit}>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Player">
            <select
              value={resolvedPlayerId}
              onChange={(e) => setPlayerId(e.target.value)}
              disabled={isEdit || rosterQuery.isLoading}
              data-testid="goal-composer-player"
              className="tktic-select"
            >
              {players.length === 0 ? (
                <option value="">No players yet</option>
              ) : (
                players.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.jersey_number
                      ? `#${p.jersey_number} ${p.display_name}`
                      : p.display_name}
                  </option>
                ))
              )}
            </select>
          </Field>
          <Field label="Context">
            <select
              value={context}
              onChange={(e) => setContext(e.target.value)}
              data-testid="goal-composer-context"
              className="tktic-select"
            >
              {CONTEXT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </Field>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <Field label="Visibility">
            <select
              value={visibility}
              onChange={(e) => setVisibility(e.target.value)}
              data-testid="goal-composer-visibility"
              className="tktic-select"
            >
              {VISIBILITY_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Priority">
            <select
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
              data-testid="goal-composer-priority"
              className="tktic-select"
            >
              {PRIORITY_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Status">
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              data-testid="goal-composer-status"
              className="tktic-select"
            >
              {STATUS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </Field>
        </div>

        <Field label="Goal title">
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            data-testid="goal-composer-title"
            autoFocus
            placeholder="Scan before receiving"
            className="w-full bg-bg-card border border-rule rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent"
          />
        </Field>
        <Field label="Action plan">
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            data-testid="goal-composer-description"
            rows={3}
            placeholder="What should the player try next?"
            className="w-full bg-bg-card border border-rule rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent"
          />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Target date" helper="YYYY-MM-DD, or leave blank.">
            <input
              type="text"
              inputMode="numeric"
              value={targetDate}
              onChange={(e) => setTargetDate(e.target.value)}
              data-testid="goal-composer-target-date"
              placeholder="2026-05-14"
              className="w-full bg-bg-card border border-rule rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent"
            />
          </Field>
          <Field label="Target match">
            <select
              value={targetMatchId}
              onChange={(e) => setTargetMatchId(e.target.value)}
              data-testid="goal-composer-target-match"
              className="tktic-select"
            >
              <option value="">— none —</option>
              {matches.map((m) => (
                <option key={m.id} value={m.id}>
                  {matchLabelById.get(m.id) ?? m.id}
                </option>
              ))}
            </select>
          </Field>
        </div>

        <Field label="Success criteria">
          <textarea
            value={successCriteria}
            onChange={(e) => setSuccessCriteria(e.target.value)}
            data-testid="goal-composer-success-criteria"
            rows={2}
            placeholder="How will we know this goal is working?"
            className="w-full bg-bg-card border border-rule rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent"
          />
        </Field>
        <Field
          label="Coach private note"
          helper="Not visible to family / player — scrubbed server-side."
        >
          <textarea
            value={coachPrivateNote}
            onChange={(e) => setCoachPrivateNote(e.target.value)}
            data-testid="goal-composer-private-note"
            rows={2}
            placeholder="Internal coaching context"
            className="w-full bg-bg-card border border-rule rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent"
          />
        </Field>

        {error ? (
          <p
            className="text-destructive font-mono text-xs"
            data-testid="goal-composer-error"
          >
            {error}
          </p>
        ) : null}

        <button
          type="submit"
          disabled={submitting}
          data-testid="goal-composer-submit"
          className="bg-accent hover:bg-accent-hover disabled:bg-bg-card disabled:text-ink-muted text-white rounded px-4 py-2 text-sm font-display uppercase tracking-wide transition-colors"
        >
          {submitting ? "Saving…" : isEdit ? "Save goal" : "Create goal"}
        </button>
      </form>
    </CoachModal>
  );
}

function Field(props: {
  label: string;
  helper?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-display uppercase tracking-wider text-ink-muted">
        {props.label}
      </span>
      {props.children}
      {props.helper ? (
        <span className="text-xs text-ink-muted">{props.helper}</span>
      ) : null}
    </label>
  );
}
